import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Opens a drill-in page and records where it was opened from, so its back button returns there: the same page, with
 * the same query string (view, tab, filters).
 * @returns {(to: string) => void}
 */
export const useDrillIn = () => {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    return useCallback((to) => navigate(to, { state: { from: `${pathname}${search}` } }), [navigate, pathname, search]);
};

/**
 * Where a drill-in page's back button goes: the place `useDrillIn` opened it from, or `fallback` when it was opened
 * any other way (a link, a refresh).
 * @param {string} fallback
 * @returns {string}
 */
export const useDrillInReturn = (fallback) => {
    const location = useLocation();

    return location.state?.from || fallback;
};

/**
 * The tab to show for a `?tab=` value. Junk or missing values open the first tab.
 * @param {{ value: string }[]} tabs
 * @param {string | null} value
 */
export const tabValueFor = (tabs, value) => (tabs.some((tab) => tab.value === value) ? value : tabs[0]?.value);

/**
 * The active tab, kept in the query string so a refresh or a shared link opens it. Switching tabs keeps the other
 * query params and the `useDrillIn` return.
 * @param {{ value: string }[]} tabs
 * @param {string} [param] the query param holding the tab
 * @returns {[string, (value: string) => void]}
 */
export const useTabParam = (tabs, param = 'tab') => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const value = tabValueFor(tabs, searchParams.get(param));

    const setValue = useCallback(
        (next) =>
            setSearchParams(
                (current) => {
                    const params = new URLSearchParams(current);
                    params.set(param, next);
                    return params;
                },
                { state: location.state }
            ),
        [setSearchParams, param, location.state]
    );

    return [value, setValue];
};
